import { useEffect, useState, useCallback } from 'react';
import { CloudUpload, RefreshCw, Loader2, CheckCircle2, XCircle, Clock, Trash2, Download, AlertCircle, Layers, ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/services/api';

const STATUS_META = {
  success: { icon: CheckCircle2, cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', label: 'Success' },
  failed: { icon: XCircle, cls: 'bg-red-100 text-red-700 border-red-200', label: 'Failed' },
  running: { icon: Loader2, cls: 'bg-blue-100 text-blue-700 border-blue-200 animate-pulse', label: 'Running' },
  pruned: { icon: Trash2, cls: 'bg-gray-100 text-gray-600 border-gray-200', label: 'Pruned' },
};

const formatDateTime = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return iso; }
};

const formatNextRun = (seconds) => {
  if (seconds === null || seconds === undefined) return '-';
  const s = Number(seconds) || 0;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
};

const TODAY_META = {
  success: { cls: 'bg-emerald-50 border-emerald-200 text-emerald-800', icon: CheckCircle2, title: "Today's automatic backup completed" },
  failed: { cls: 'bg-red-50 border-red-200 text-red-800', icon: XCircle, title: "Today's backup FAILED" },
  missed: { cls: 'bg-red-50 border-red-200 text-red-800', icon: AlertCircle, title: "Today's scheduled backup did not run" },
  running: { cls: 'bg-blue-50 border-blue-200 text-blue-800', icon: Loader2, title: 'Backup is running…' },
  pending: { cls: 'bg-gray-50 border-gray-200 text-gray-700', icon: Clock, title: "Today's automatic backup is scheduled" },
  not_configured: { cls: 'bg-amber-50 border-amber-200 text-amber-800', icon: AlertCircle, title: 'Bunny CDN is not configured' },
};

const Stat = ({ label, children, title }) => (
  <div className="p-3 rounded-lg bg-gray-50 border">
    <p className="text-xs text-gray-500">{label}</p>
    <p className="font-semibold text-gray-800 truncate" title={title}>{children}</p>
  </div>
);

const AutoBackupPanel = () => {
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, st, hist] = await Promise.all([
        api.get('/system/backup/config'),
        api.get('/system/backup/status'),
        api.get('/system/backup/history?limit=15'),
      ]);
      setConfig(cfg.data);
      setStatus(st.data);
      setHistory((hist.data?.items || []).filter(x => STATUS_META[x?.status]));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load backup info');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true);
    try {
      const res = await api.post('/system/backup/auto-run');
      toast.success(`Backup uploaded to Bunny CDN — ${res.data.collections_count} collections, ${res.data.documents_total} records (${res.data.size_kb} KB)`);
      window.dispatchEvent(new Event('backup-status-changed'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Backup failed');
      window.dispatchEvent(new Event('backup-status-changed'));
      load();
    } finally {
      setRunning(false);
    }
  };

  const today = status ? (TODAY_META[status.state] || TODAY_META.pending) : null;
  const TodayIcon = today?.icon;

  return (
    <Card className="border-blue-200" data-testid="auto-backup-panel">
      <CardHeader className="bg-blue-50/60 border-b border-blue-100">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2 text-blue-800">
              <CloudUpload className="w-5 h-5" /> Automatic Daily Backup to Bunny CDN
            </CardTitle>
            <CardDescription className="text-blue-700">
              Every collection in the database is backed up daily at {config?.schedule_local || '00:30'} ({config?.schedule_tz || 'Asia/Kolkata'}).
              Backups from the last {config?.keep_days ?? 3} days are kept on Bunny CDN. New collections are included automatically.
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="refresh-backup-btn">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button
              size="sm"
              onClick={runNow}
              disabled={running || !config?.is_configured}
              data-testid="run-backup-now-btn"
            >
              {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CloudUpload className="w-4 h-4 mr-2" />}
              {running ? 'Backing up…' : 'Backup Now to Bunny CDN'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        {today && (
          <div className={`p-3 rounded-lg border flex items-start gap-3 ${today.cls}`} data-testid="backup-today-status" data-state={status.state}>
            <TodayIcon className={`w-5 h-5 mt-0.5 ${status.state === 'running' ? 'animate-spin' : ''}`} />
            <div className="text-sm">
              <p className="font-semibold">{today.title}</p>
              {status.state === 'success' && status.today_success && (
                <p>{formatDateTime(status.today_success.started_at)} · {status.today_success.collections_count} collections · {status.today_success.documents_total} records · {status.today_success.size_kb} KB</p>
              )}
              {status.state === 'failed' && status.today_failed && (
                <p>{formatDateTime(status.today_failed.started_at)} — {status.today_failed.error}</p>
              )}
              {status.state === 'missed' && (
                <p>Expected at {status.schedule_local} {status.schedule_tz}. Run a manual backup now.</p>
              )}
              {status.state === 'pending' && (
                <p>Runs at {status.schedule_local} {status.schedule_tz} (in {formatNextRun(status.next_run_in_seconds)}).</p>
              )}
              {status.last_success && status.state !== 'success' && (
                <p className="text-xs mt-1 opacity-80">Last successful backup: {formatDateTime(status.last_success.started_at)} ({status.last_success.file_name})</p>
              )}
            </div>
          </div>
        )}

        {config && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <Stat label="Bunny Zone" title={config.storage_zone || '-'}>{config.storage_zone || '—'}</Stat>
            <Stat label="Region"><span className="uppercase">{config.region || 'default'}</span></Stat>
            <Stat label="Schedule">{config.schedule_local} <span className="text-xs text-gray-500">{config.schedule_tz}</span></Stat>
            <Stat label="Next Run In"><span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {formatNextRun(config.next_run_in_seconds)}</span></Stat>
            <Stat label="Retention">Last {config.keep_days} days</Stat>
            <Stat label="Collections Covered" title={`${config.collections_total} collections in database`}>
              <span className="flex items-center gap-1" data-testid="backup-coverage">
                <Layers className="w-3.5 h-3.5" /> {config.last_backup_collections}/{config.collections_total}
              </span>
            </Stat>
          </div>
        )}

        {config && config.not_yet_backed_up?.length > 0 && config.last_backup_collections > 0 && (
          <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800" data-testid="backup-new-collections">
            <span className="font-semibold">{config.not_yet_backed_up.length} collection(s) added since the last backup</span> — they will be included in the next run:
            {' '}{config.not_yet_backed_up.join(', ')}
          </div>
        )}

        {config && config.collections_total > 0 && config.last_backup_collections === config.collections_total && (
          <div className="flex items-center gap-2 text-xs text-emerald-700" data-testid="backup-full-coverage">
            <ShieldCheck className="w-4 h-4" /> Last backup covered all {config.collections_total} collections.
          </div>
        )}

        {config && !config.is_configured && (
          <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5" />
            Bunny CDN is not configured for this workspace. Configure Storage under Business Settings first.
          </div>
        )}

        <div className="border rounded-lg overflow-hidden">
          <div className="bg-gray-50 border-b px-3 py-2 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-800">Backup Runs</span>
            <span className="text-xs text-gray-500">Scheduled and manual</span>
          </div>
          <div className="max-h-[360px] overflow-y-auto">
            {history.length === 0 ? (
              <div className="py-8 text-center text-sm text-gray-500">No backup runs yet.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-600 border-b">
                  <tr className="text-left">
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">File</th>
                    <th className="px-3 py-2">Started</th>
                    <th className="px-3 py-2">Trigger</th>
                    <th className="px-3 py-2 text-right">Collections</th>
                    <th className="px-3 py-2 text-right">Records</th>
                    <th className="px-3 py-2 text-right">Size</th>
                    <th className="px-3 py-2 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(h => {
                    const meta = STATUS_META[h.status];
                    const Icon = meta.icon;
                    return (
                      <tr key={h.id} className="border-b align-top" data-testid={`backup-row-${h.file_name}`}>
                        <td className="px-3 py-2">
                          <Badge variant="outline" className={`text-xs ${meta.cls}`}>
                            <Icon className="w-3 h-3 mr-1" /> {meta.label}
                          </Badge>
                          {h.status === 'failed' && h.error && (
                            <p className="text-[11px] text-red-600 mt-1 max-w-[220px]">{h.error}</p>
                          )}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{h.file_name}</td>
                        <td className="px-3 py-2 text-xs text-gray-600">{formatDateTime(h.started_at)}</td>
                        <td className="px-3 py-2 text-xs text-gray-600">{h.triggered_by || '-'}</td>
                        <td className="px-3 py-2 text-right text-xs">{h.collections_count ?? '-'}</td>
                        <td className="px-3 py-2 text-right text-xs">{h.documents_total ?? '-'}</td>
                        <td className="px-3 py-2 text-right text-xs">{h.size_kb ? `${h.size_kb} KB` : '-'}</td>
                        <td className="px-3 py-2 text-center">
                          {h.cdn_url && h.status === 'success' ? (
                            <a
                              href={h.cdn_url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center text-blue-600 hover:text-blue-800 text-xs"
                              data-testid={`download-backup-${h.file_name}`}
                            >
                              <Download className="w-3.5 h-3.5 mr-1" /> Download
                            </a>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AutoBackupPanel;
